/**
 * The rules of "Send for training" (plan docs/plans/lms-plan.md 11.2, 8.1,
 * 8.2, 18.25; Appendix A POST /admin/sops/:id/versions/:vid/send-for-training;
 * WP-46), kept out of the dialog so they read in one place. No React, no I/O.
 *
 * The four steps (11.2): 1 the mode (Read and acknowledge, the default, or
 * Quiz), 2 the retrain policy with 8.1's wording (default "Everyone retrains
 * now" for an SOP change), 3 the audience and due window through the Assign
 * wizard's audience step (WP-43's AudienceBuilder), 4 review and Send.
 *
 * What the server does with the body (18.25): the course is created on the
 * first send with the mode as its kind (validity_months from the dialog,
 * default 24), so a later send's mode must match it (409 { course_kind });
 * version 1 of the training publishes only as at_renewal (8.1, 18.17: nobody
 * holds it yet); a quiz-mode send leaves a DRAFT for the course editor and
 * stores no audience, so the audience is sent again, with the same route, once
 * that draft is published; change_summary is needed when the SOP version has
 * none and the training version is 2 or later; estimated_minutes defaults to
 * the lint's estimate.
 */
import type { RetrainPolicy } from '../../api/catalog';
import type { CampaignAudience } from '../../api/campaigns';
import type { SendSopForTrainingInput, SopDetailSop, SopDetailVersion, SopSendMode } from '../../api/sops';
import {
  SOP_DEFAULT_VALIDITY_MONTHS,
  SOP_ESTIMATED_MINUTES_MAX,
  SOP_ESTIMATED_MINUTES_MIN,
  SOP_VALIDITY_MONTHS_MAX,
  SOP_VALIDITY_MONTHS_MIN,
} from '../../api/sops';
import { parseWholeDays } from '../../components/pickers/shared';

/** 11.2's four steps, in order. */
export const SEND_STEPS = ['Mode', 'Retrain policy', 'Audience and due date', 'Review and send'] as const;
export type SendStep = 0 | 1 | 2 | 3;

/** 8.2: a due window of whole days, 1 to 365; 8.1: a retrain assignment falls due within a year. */
export const DUE_DAYS_MIN = 1;
export const DUE_DAYS_MAX = 365;

/**
 * Which send this is:
 *   'send'        the four steps for a version with no training yet
 *   'send_people' the training version is already published (a quiz draft published in the
 *                 editor, or a read-and-acknowledge send that stopped before its campaign):
 *                 the mode and the policy are fixed, the audience is sent now
 */
export type SendKind = 'send' | 'send_people';

/** The facts the dialog reads from the SOP and the version. */
export interface SendContext {
  kind: SendKind;
  /** The course kind once the first send has created the course (11.2), else null. */
  lockedMode: SopSendMode | null;
  /** No training version of this SOP has been published yet: nobody holds it (8.1: at_renewal only). */
  firstTraining: boolean;
  /** The SOP version carries no change summary and the training version will be 2 or later (18.25). */
  needsChangeSummary: boolean;
  /**
   * The course, once the first send has created it: POST /admin/campaigns/preview then says who a send
   * reaches. Before it (null) the send route previews itself (preview: true; useFirstSendPreview).
   */
  courseId: string | null;
  /** send_people: the published training version the campaign pins. */
  publishedVersionId: string | null;
  publishedPolicy: RetrainPolicy | null;
  publishedVersionNo: number | null;
}

export function sendContextOf(sop: SopDetailSop, version: SopDetailVersion, kind: SendKind): SendContext {
  const course = sop.course ?? null;
  const cv = version.course_version ?? null;
  return {
    kind,
    lockedMode: course ? course.kind : null,
    firstTraining: !course || !course.current_version_id,
    needsChangeSummary: kind === 'send' && Boolean(course) && !(version.change_summary ?? '').trim(),
    courseId: course ? course.id : null,
    publishedVersionId: kind === 'send_people' && cv ? cv.id : null,
    publishedPolicy: kind === 'send_people' && cv ? cv.retrain_policy : null,
    publishedVersionNo: kind === 'send_people' && cv ? cv.version_no : null,
  };
}

/** Whether this send goes out to people now (11.2: a fresh quiz-mode send only creates the draft). */
export function sendsCampaign(ctx: SendContext, mode: SopSendMode): boolean {
  return ctx.kind === 'send_people' || mode === 'read_ack';
}

/** Whether step 2 asks for a policy (a fresh read-and-acknowledge send of a training others already hold). */
export function asksPolicy(ctx: SendContext, mode: SopSendMode): boolean {
  return ctx.kind === 'send' && mode === 'read_ack' && !ctx.firstTraining;
}

/** 8.1: the two policies that create assignments take a due window. */
export function policyTakesDueDays(policy: RetrainPolicy | null): boolean {
  return policy === 'immediate' || policy === 'acknowledge';
}

/** A whole number typed into a field within [min, max], or null ('' is null too). */
export function parseWhole(raw: string, min: number, max: number): number | null {
  return parseWholeDays(raw, min, max);
}

export interface SendForm {
  mode: SopSendMode;
  /** '' means: keep the course's validity (an existing course) or the default (a new one). */
  validityMonths: string;
  estimatedMinutes: string;
  changeSummary: string;
  policy: RetrainPolicy | null;
  retrainDueDays: string;
  audience: CampaignAudience;
  dueDays: string;
  message: string;
  force: boolean;
  /** The people unticked in the preview. */
  excluded: string[];
}

/** The first step's problems, in words (empty when it may go on). */
export function modeStepProblems(ctx: SendContext, form: SendForm): string[] {
  if (ctx.kind === 'send_people') return [];
  const out: string[] = [];
  if (ctx.courseId === null || form.validityMonths.trim() !== '') {
    if (parseWhole(form.validityMonths, SOP_VALIDITY_MONTHS_MIN, SOP_VALIDITY_MONTHS_MAX) === null) {
      out.push(`Enter a validity of ${SOP_VALIDITY_MONTHS_MIN} to ${SOP_VALIDITY_MONTHS_MAX} whole months.`);
    }
  }
  if (form.estimatedMinutes.trim() !== '' && parseWhole(form.estimatedMinutes, SOP_ESTIMATED_MINUTES_MIN, SOP_ESTIMATED_MINUTES_MAX) === null) {
    out.push(`Enter the minutes as a whole number from ${SOP_ESTIMATED_MINUTES_MIN} to ${SOP_ESTIMATED_MINUTES_MAX}, or leave it empty.`);
  }
  if (ctx.needsChangeSummary && !form.changeSummary.trim()) {
    out.push('Say what changed: this SOP version has no change summary and the training has an earlier version.');
  }
  return out;
}

export function policyStepProblems(ctx: SendContext, form: SendForm): string[] {
  if (!asksPolicy(ctx, form.mode)) return [];
  if (!form.policy) return ['Choose what happens to people who already hold this training.'];
  if (policyTakesDueDays(form.policy) && parseWhole(form.retrainDueDays, DUE_DAYS_MIN, DUE_DAYS_MAX) === null) {
    return [`Enter a retrain window of ${DUE_DAYS_MIN} to ${DUE_DAYS_MAX} whole days.`];
  }
  return [];
}

/**
 * The POST body (Appendix A plus 18.25's fields). A fresh read-and-acknowledge
 * send carries the policy (at_renewal for the first training version), the
 * retrain window when the policy takes one, the validity (always for a new
 * course; only when typed for an existing one), the audience, the due window,
 * the message when there is one, force, the unticked people still in the
 * preview, the change summary when it is needed, and the minutes when typed.
 * A fresh quiz-mode send carries the course facts only (the draft is made;
 * no audience is stored). A send of an already published training version
 * carries the mode and the audience part.
 */
export function buildSendBody(ctx: SendContext, form: SendForm, previewIds: string[] | null): SendSopForTrainingInput {
  const inPreview = previewIds ? new Set(previewIds) : null;
  const audiencePart = (): Partial<SendSopForTrainingInput> => {
    const part: Partial<SendSopForTrainingInput> = {
      audience: {
        everyone_at_sites: [...form.audience.everyone_at_sites],
        platform_roles: [...form.audience.platform_roles],
        group_ids: [...form.audience.group_ids],
        user_ids: [...form.audience.user_ids],
      },
      due_days: parseWhole(form.dueDays, DUE_DAYS_MIN, DUE_DAYS_MAX) ?? undefined,
      force: form.force,
      exclude_user_ids: form.excluded.filter((id) => !inPreview || inPreview.has(id)),
    };
    const message = form.message.trim();
    if (message) part.message = message;
    return part;
  };

  if (ctx.kind === 'send_people') {
    return { mode: ctx.lockedMode ?? form.mode, ...audiencePart() };
  }

  const mode = ctx.lockedMode ?? form.mode;
  const body: SendSopForTrainingInput = { mode };
  if (ctx.courseId === null) {
    body.validity_months = parseWhole(form.validityMonths, SOP_VALIDITY_MONTHS_MIN, SOP_VALIDITY_MONTHS_MAX) ?? SOP_DEFAULT_VALIDITY_MONTHS;
  } else if (form.validityMonths.trim() !== '') {
    const months = parseWhole(form.validityMonths, SOP_VALIDITY_MONTHS_MIN, SOP_VALIDITY_MONTHS_MAX);
    if (months !== null) body.validity_months = months;
  }
  const minutes = form.estimatedMinutes.trim() === '' ? null : parseWhole(form.estimatedMinutes, SOP_ESTIMATED_MINUTES_MIN, SOP_ESTIMATED_MINUTES_MAX);
  if (minutes !== null) body.estimated_minutes = minutes;
  if (ctx.needsChangeSummary && form.changeSummary.trim()) body.change_summary = form.changeSummary.trim();

  if (mode === 'quiz') return body;

  const policy: RetrainPolicy = ctx.firstTraining ? 'at_renewal' : form.policy ?? 'immediate';
  body.retrain_policy = policy;
  if (!ctx.firstTraining && policyTakesDueDays(policy)) {
    const days = parseWhole(form.retrainDueDays, DUE_DAYS_MIN, DUE_DAYS_MAX);
    if (days !== null) body.retrain_due_days = days;
  }
  return { ...body, ...audiencePart() };
}
